import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, View, Text, Platform, useWindowDimensions } from 'react-native';
import {
  SLIDER_POSITION,
  GAME_TYPES_TYPE,
  GAME_TYPES_CONFIG,
  OPERAND_TYPE,
  TAnswerRow,
  operandText,
  TAnswerPair,
} from '../../../constants/constants';
import { DivideIcon } from '../../../resources/svgComponents/DivideIcon';
import { MinusIcon } from '../../../resources/svgComponents/MinusIcon';
import { PlusIcon } from '../../../resources/svgComponents/PlusIcon';
import { XIcon } from '../../../resources/svgComponents/XIcon';
import { LargeSlider } from '../LargeSlider';
import { getDefaultOperands, renderValue } from '../../screens/GameScreen/utils';
import {
  checkUserAnswerAssessment,
  generateAnswerTableAssessment,
} from '../../../utils/journeysUtils';
import Feather from 'react-native-vector-icons/Feather';
import { useIncrementStreakOnGamePlayMutation } from '../../../graphql/useIncrementStreakOnGamePlayMutation.gql';
import { useSignedInContext } from '../../../context/SignedInContext';
import { EGymAssessmentMode } from '../../../utils/gymUtils';
import { useGetHivesQuery } from '../../screens/MyHives/useGetHivesQuery.gql';

type TAssessmentTemplateProps = {
  gameType: GAME_TYPES_TYPE;
  questionsCount?: number;
  setQuestionIndex?: (index: number) => void;
  setScore?: (count: number) => void;
  onComplete: ({
    finalScore,
    correctAnswers,
    wrongAnswers,
  }: {
    finalScore: number;
    correctAnswers?: TAnswerPair[];
    wrongAnswers?: TAnswerPair[];
  }) => void;
  mode?: EGymAssessmentMode;
  setCorrectAnswers?: React.Dispatch<React.SetStateAction<TAnswerPair[]>>;
  setWrongAnswers?: React.Dispatch<React.SetStateAction<TAnswerPair[]>>;
};

export default function AssessmentTemplate(props: TAssessmentTemplateProps) {
  const {
    gameType,
    questionsCount = 25,
    setQuestionIndex = () => {},
    setScore = () => {},
    onComplete,
    mode = EGymAssessmentMode.FixedQuestions,
    setCorrectAnswers: setCorrectAnswersParent,
    setWrongAnswers: setWrongAnswersParent,
  } = props;
  const { user } = useSignedInContext();
  const userId = user?.id;

  const [answerTable, setAnswerTable] = useState<TAnswerRow[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<TAnswerRow | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [incrementStreak] = useIncrementStreakOnGamePlayMutation();

  const [firstSlider, setFirstSlider] = useState<OPERAND_TYPE>(
    getDefaultOperands(gameType).leftOperand,
  );
  const [secondSlider, setSecondSlider] = useState<OPERAND_TYPE>(
    getDefaultOperands(gameType).rightOperand,
  );
  const [activePair, setActivePair] = useState<TAnswerPair | null>(null);

  const [attempt, setAttempt] = useState(0);
  const [feedback, setFeedback] = useState<{
    msg: string;
    backgroundColor: string;
    correct: boolean;
  } | null>(null);

  const { width } = useWindowDimensions();
  const isMobile = Platform.OS !== 'web';
  const isMobileWeb = Platform.OS === 'web' && width <= 768;

  const allowedAttempts = mode === EGymAssessmentMode.FixedQuestions ? 2 : 1;
  const allowedCorrectAttempts = 1;

  // trcking of correct and wrong answers for gym analytics
  const [correctAnswers, setCorrectAnswers] = useState<TAnswerPair[]>([]);
  const [wrongAnswers, setWrongAnswers] = useState<TAnswerPair[]>([]);

  // Generate next question
  const generateNextQuestion = useCallback(
    (
      table?: TAnswerRow[],
      prevSelection?: { left: OPERAND_TYPE; right: OPERAND_TYPE },
      prevQuestion?: TAnswerRow,
    ) => {
      const currentTable = table || answerTable;
      if (!currentTable || currentTable.length === 0) return;

      let candidates: TAnswerRow[] = [];
      let selectedPair: { left: OPERAND_TYPE; right: OPERAND_TYPE } | undefined;
      let questionRow: TAnswerRow | undefined;

      if (prevSelection && prevQuestion) {
        const continueFromRight = Math.random() < 0.5;

        if (continueFromRight) {
          const operandToMatch = prevSelection.right;

          candidates = currentTable.filter(
            row =>
              row !== prevQuestion && row.pairs.some(pair => isEqual(pair.right, operandToMatch)),
          );

          questionRow = candidates[Math.floor(Math.random() * candidates.length)];

          selectedPair = questionRow?.pairs.find(pair => isEqual(pair.right, operandToMatch));
        } else {
          const operandToMatch = prevSelection.left;

          candidates = currentTable.filter(
            row =>
              row !== prevQuestion && row.pairs.some(pair => isEqual(pair.left, operandToMatch)),
          );

          questionRow = candidates[Math.floor(Math.random() * candidates.length)];

          selectedPair = questionRow?.pairs.find(pair => isEqual(pair.left, operandToMatch));
        }
      } else {
        candidates = prevQuestion ? currentTable.filter(row => row !== prevQuestion) : currentTable;
        questionRow = candidates[Math.floor(Math.random() * candidates.length)];
        selectedPair = questionRow?.pairs[0];
      }

      if (!questionRow || !selectedPair) return;

      setCurrentQuestion(questionRow);
      setActivePair(selectedPair);
      setAttempt(0);

      if (prevSelection) {
        setFirstSlider(prevSelection.left);
        setSecondSlider(prevSelection.right);
      } else if (selectedPair) {
        const leftOperands =
          GAME_TYPES_CONFIG[gameType].leftOperands || GAME_TYPES_CONFIG[gameType].operands;
        const rightOperands =
          GAME_TYPES_CONFIG[gameType].rightOperands || GAME_TYPES_CONFIG[gameType].operands;

        if (Math.random() < 0.5) {
          setFirstSlider(selectedPair.left);
          setSecondSlider(rightOperands[Math.floor(Math.random() * rightOperands.length)]);
        } else {
          setFirstSlider(leftOperands[Math.floor(Math.random() * leftOperands.length)]);
          setSecondSlider(selectedPair.right);
        }
      }
    },
    [
      answerTable,
      gameType,
      setCurrentQuestion,
      setActivePair,
      setAttempt,
      setFirstSlider,
      setSecondSlider,
    ],
  );

  function isEqual(o1: OPERAND_TYPE, o2: OPERAND_TYPE): boolean {
    return JSON.stringify(o1) === JSON.stringify(o2);
  }

  useEffect(() => {
    const leftOperands =
      GAME_TYPES_CONFIG[gameType].leftOperands || GAME_TYPES_CONFIG[gameType].operands;
    const rightOperands =
      GAME_TYPES_CONFIG[gameType].rightOperands || GAME_TYPES_CONFIG[gameType].operands;
    const table = generateAnswerTableAssessment(leftOperands, rightOperands, gameType);
    setAnswerTable(table);

    setCurrentIndex(0);
    setCorrectCount(0);
    setWrongCount(0);
    setCorrectAnswers([]);
    setWrongAnswers([]);
    setCorrectAnswersParent?.([]);
    setWrongAnswersParent?.([]);

    generateNextQuestion(table);
  }, [gameType]);

  const checkSelection = (left: OPERAND_TYPE, right: OPERAND_TYPE) => {
    if (!currentQuestion || !activePair) return;

    const currentLeft = firstSlider;
    const currentRight = secondSlider;

    const isCorrect = checkUserAnswerAssessment(currentQuestion, left, right);

    if (isCorrect) {
      let updatedCorrectCount = correctCount;
      let updatedCorrectAnswers = correctAnswers;

      if (attempt < allowedCorrectAttempts) {
        updatedCorrectCount = correctCount + 1;
        setCorrectCount(updatedCorrectCount);
        setScore(updatedCorrectCount);

        if (mode) {
          updatedCorrectAnswers = [...correctAnswers, activePair];
          setCorrectAnswers(updatedCorrectAnswers);
          setCorrectAnswersParent?.(updatedCorrectAnswers);
        }
      }

      setFeedback({ msg: 'Correct, Great Job!', backgroundColor: '#3E9D0C', correct: true });

      setTimeout(async () => {
        setFeedback(null);
        const nextIndex = currentIndex + 1;
        setCurrentIndex(nextIndex);
        setQuestionIndex(nextIndex);

        if (nextIndex < questionsCount || mode === EGymAssessmentMode.Timed) {
          generateNextQuestion(undefined, { left, right }, currentQuestion);
        } else {
          onComplete({
            finalScore: updatedCorrectCount,
            correctAnswers: updatedCorrectAnswers,
            wrongAnswers,
          });

          if (userId) {
            await incrementStreak({
              variables: { userId: userId },
            });
          }
        }
      }, 1000);
    } else {
      const nextAttempt = attempt + 1;

      if (nextAttempt < allowedAttempts) {
        setAttempt(nextAttempt);
        setFeedback({
          msg: 'Incorrect answer. Have another try',
          backgroundColor: '#E53232',
          correct: false,
        });

        setTimeout(() => {
          setFirstSlider(currentLeft);
          setSecondSlider(currentRight);
          setFeedback(null);
        }, 1000);
      } else {
        const updatedWrongAnswers = [...wrongAnswers, activePair];
        setWrongCount(w => w + 1);

        if (mode) {
          setWrongAnswers(updatedWrongAnswers);
          setWrongAnswersParent?.(updatedWrongAnswers);
        }

        setFeedback({
          msg: `Incorrect answer, it was ${renderValue(activePair.left, gameType)} ${
            operandText[GAME_TYPES_CONFIG[gameType].operation]
          } ${renderValue(activePair.right, gameType)}`,
          backgroundColor: '#E53232',
          correct: false,
        });

        setTimeout(async () => {
          setFeedback(null);
          const nextIndex = currentIndex + 1;
          setCurrentIndex(nextIndex);
          setQuestionIndex(nextIndex);

          if (nextIndex < questionsCount || mode === EGymAssessmentMode.Timed) {
            generateNextQuestion(undefined, { left, right }, currentQuestion);
          } else {
            onComplete({
              finalScore: correctCount,
              correctAnswers,
              wrongAnswers: updatedWrongAnswers,
            });

            if (userId) {
              await incrementStreak({
                variables: { userId: userId },
              });
            }
          }
        }, 1500);
      }
    }
  };

  const onChangeFirstSlider = (val: OPERAND_TYPE) => {
    setFirstSlider(val);
    checkSelection(val, secondSlider);
  };

  const onChangeSecondSlider = (val: OPERAND_TYPE) => {
    setSecondSlider(val);
    checkSelection(firstSlider, val);
  };

  if (!currentQuestion) return <Text>Loading...</Text>;

  return (
    <View
      style={[
        styles.mainContainer,
        isMobile && { marginTop: 30 },
        isMobileWeb && { marginTop: 20 },
      ]}
    >
      <View
        style={[
          styles.answerContainer,
          {
            height: isMobile ? 86 : 123,
            width: isMobile ? 322 : 682,
            marginBottom: isMobile ? 70 : 103,
          },
          isMobileWeb && { marginBottom: 60 },
        ]}
      >
        <Text style={styles.answerText}>{renderValue(currentQuestion.answer, gameType)}</Text>
        {feedback && (
          <View style={styles.feedbackContainer}>
            <View style={[styles.feedbackContent, { backgroundColor: feedback.backgroundColor }]}>
              <Feather
                name={feedback.correct ? 'check-circle' : 'x-circle'}
                size={20}
                color="#FFFFFF"
              />
              <Text style={styles.feedbackText}>{feedback.msg}</Text>
            </View>
          </View>
        )}
      </View>

      <View style={styles.controlsContainer}>
        <View style={styles.mobileRow}>
          <View style={styles.leftSlider}>
            <LargeSlider
              colors={['#FFAA1B', '#FFAA1B']}
              value={firstSlider}
              setValue={onChangeFirstSlider}
              gameType={gameType}
              position={SLIDER_POSITION.LEFT}
              operandRowButtonCount={GAME_TYPES_CONFIG[gameType].leftOperandRowButtonCount}
            />
          </View>

          {gameType === GAME_TYPES_TYPE.ADD_1_12 ||
          gameType === GAME_TYPES_TYPE.ADD_MINUS_6_6 ||
          gameType === GAME_TYPES_TYPE.ADD_ALGEBRA ? (
            <PlusIcon containerStyle={{ padding: 8, paddingTop: 0 }} size={20} />
          ) : gameType === GAME_TYPES_TYPE.SUBTRACT_INTEGERS ||
            gameType === GAME_TYPES_TYPE.SUBTRACT_POSITIVE_INTEGERS ? (
            <MinusIcon containerStyle={{ padding: 8, paddingTop: 0 }} size={20} />
          ) : gameType === GAME_TYPES_TYPE.DIVIDE ? (
            <DivideIcon containerStyle={{ padding: 8, paddingTop: 0 }} size={20} />
          ) : (
            <XIcon containerStyle={{ padding: 8, paddingTop: 0 }} width={20} height={20} />
          )}

          <View style={styles.rightSlider}>
            <LargeSlider
              colors={['#FFAA1B', '#FFAA1B']}
              value={secondSlider}
              setValue={onChangeSecondSlider}
              gameType={gameType}
              position={SLIDER_POSITION.RIGHT}
              operandRowButtonCount={GAME_TYPES_CONFIG[gameType].rightOperandRowButtonCount}
            />
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  mainContainer: {
    marginTop: 100,
    position: 'relative',
    borderRadius: 16,
    overflow: 'hidden',
    alignItems: 'center',
  },
  controlsContainer: {
    position: 'relative',
    backgroundColor: '#FFEABA',
    marginTop: 20,
    paddingVertical: 24,
    paddingHorizontal: 60,
    borderRadius: 8,
  },
  answerContainer: {
    maxWidth: 682,
    backgroundColor: '#FFF5DD',
    borderColor: '#FFCF53',
    borderRadius: 8,
    borderBottomWidth: 4,
    borderRightWidth: 4,
    borderTopWidth: 1,
    borderLeftWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 45,
    position: 'relative',
  },
  answerText: {
    fontFamily: 'Secular One',
    fontWeight: '400',
    fontSize: 45,
  },
  mobileRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  leftSlider: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  rightSlider: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  feedbackContainer: {
    position: 'absolute',
    bottom: -90,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 10,
    justifyContent: 'center',
  },
  feedbackContent: {
    height: 56,
    minWidth: 228,
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 10,
  },
  feedbackText: {
    fontSize: 14,
    fontWeight: '400',
    color: '#FFFFFF',
    marginLeft: 10,
  },
});
